import { useEffect } from 'react';
import { useGameStore } from '@/state/gameStore';
import { ACHIEVEMENTS } from '@/domain/achievements';
import { Icon, type IconName } from '@/ui/components/Icon';
import './AchievementToast.css';

/**
 * Slide-in toast queue for newly unlocked achievements. Reads from
 * `gameStore.pendingToasts`, auto-dismisses each after ~4 s, and stacks
 * vertically if multiple unlock at once (e.g. a Vault-clear that crosses
 * `first_vault` AND `vaults_25` boundaries simultaneously).
 *
 * Mounted once at AppShell-root. Self-contained — no props, no manual
 * lifecycle to coordinate.
 */

const DISMISS_AFTER_MS = 4200;

export function AchievementToasts() {
  const toasts = useGameStore((s) => s.pendingToasts);
  const dismiss = useGameStore((s) => s.dismissToast);

  // Auto-dismiss each toast after the timeout. Effect re-runs when the
  // queue grows; the cleanup clears any previous timer for the same id
  // so we don't double-dismiss.
  useEffect(() => {
    const timers = toasts.map((t) =>
      window.setTimeout(() => dismiss(t.id), DISMISS_AFTER_MS),
    );
    return () => {
      for (const tid of timers) window.clearTimeout(tid);
    };
  }, [toasts, dismiss]);

  if (toasts.length === 0) return null;

  return (
    <div className="achievement-toast-stack" aria-live="polite">
      {toasts.map((toast) => {
        const def = ACHIEVEMENTS[toast.achId];
        if (!def) return null;
        const iconName = (def.iconKey as IconName) || 'sparkles';
        return (
          <button
            key={toast.id}
            type="button"
            className="achievement-toast"
            onClick={() => dismiss(toast.id)}
            title="Schließen"
          >
            <span className="achievement-toast__icon">
              <Icon name={iconName} size={28} />
            </span>
            <div className="achievement-toast__body">
              <div className="achievement-toast__label">Erfolg freigeschaltet</div>
              <div className="achievement-toast__name">{def.id}</div>
              {def.reward.neonGems ? (
                <div className="achievement-toast__reward">+{def.reward.neonGems} ◆ · im Hero-Tab abholen</div>
              ) : def.reward.cosmeticId ? (
                <div className="achievement-toast__reward">Belohnung im Hero-Tab abholen</div>
              ) : null}
            </div>
          </button>
        );
      })}
    </div>
  );
}
